/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import { getSnapshot } from 'mobx-state-tree';
/**
 * a set of APIs to evaluate and change compare table content (access the compareTableSummary model)
 * @module CompareTableAPI
 */
export default class CompareTableAPI {
	constructor(compareTableSummary) {

		/**
		 * To be invoked by the product only.
		 */
		this.destroy = function() {
			compareTableSummary = null;
		};

		/**
		 * @return {object} Snapshot of the state
		 */
		this.getState = function() {
			return getSnapshot(compareTableSummary);
		};

		/**
		 *  @return the applicable calcs as an array (eg. ['min', 'max', 'percentVsAllData', 'total'])
		 */
		this.getApplicableCalcs = function() {
			return compareTableSummary.applicableCalcs.toJSON();
		};

		/**
		 * Get the data for row from the chartStats table (where row is the nth row of the table and the value corresponds to the nth applicable calc)
		 * @param {integer} row - a number from 0 to the number of applicable calcs (as returned by getApplicableCalcs)
		 * @param {object} chartStats - the stats for one chart (eg. as returned by waitForNewStats)
		 * @return a JSON object that stores data for fields such as value, valueUse and tooltip
		 */
		this.getData = function(row, chartStats) {
			return compareTableSummary.getData(row, chartStats);
		};

		/**
		 * This function allows a caller to "spy on" updates to data for a particular widget in the compare table.
		 * See details in CompareTableSummary.
		 * @param {function} fnSpyNewStats - a function which will be invoked when stats for widgets have data (possibly unchanged)
		 * 									Use: getWidgetDataVersions() to determine the state of each widget.
		*/
		this.setSpyNewStats = function(fnSpyNewStats) {
			compareTableSummary.setSpyNewStats(fnSpyNewStats);
		};

		/**
		 * This function allows a caller to "spy on" requests to get calculation data.
		 * @param {function} fnSpyNewCalcs - a function which will be invoked when calculations are requested.
		 * 									Use: areCalculationsReady() to determine if they're up-to-date.
		*/

		this.setSpyNewCalcs = function(fnSpyNewCalcs) {
			compareTableSummary.setSpyNewCalcs(fnSpyNewCalcs);
		};

		/**
		 * @returns the widgetDataVersions which is a map of widgetAlias to version (count of updates)
		 */
		this.getWidgetDataVersions = function() {
			return compareTableSummary.getWidgetDataVersions();
		};

		/**
		 * @returns true if calculations are in a ready state.
		 */
		this.areCalculationsReady = function() {
			return compareTableSummary.areCalculationsReady();
		};

		/** 
		 * Equivalent api to selecting a new measure from the measures droplist on the compareTable.
		 * @param widgetId - the widgetId of the droplist to change
		 * @param measureName - a measureName (id) of form columnId_aggregate_layer
		*/
		this.recomputeSummaryInfo = function(widgetId, measureName) {
			return compareTableSummary.recomputeSummaryInfo({ widgetId, measureName });
		};
	}
}



// WEBPACK FOOTER //
// ./src/js/api/CompareTableAPI.js